import { useMemo } from 'react'
import { formatClock, isCaptureLive } from '@racedash/shared'
import { driverName, enteredDrivers } from '../../dashboard/screenContext'
import {
  cannotDrive,
  driverBudgets,
  expectedStints,
  formatDuration,
  frameFor,
  plannedStints,
  resolveWindows,
  stintCeiling,
  stintDuration,
  windowStateAt,
  type DriverStint,
  type Regime,
  type ResolvedWindow,
} from '@racedash/shared'
import type { DashboardData } from '../../dashboard/useSessionData'
import type { ScreenContext } from '../../dashboard/screenContext'
import { useDriverStints } from '../../dashboard/useDriverStints'
import { axisWidth, hourTicks, pct, spanning, type Span } from '../timeAxis'
import '../stints/stints.css'
import './planner.css'

/**
 * The plan against what actually happened.
 *
 * Deliberately the stint chart's axis and the stint chart's furniture, because
 * the question this screen answers is a comparison: a plan is only useful held
 * against the car. Two charts drawn differently would have to be translated
 * between before they could be compared, which is exactly the arithmetic nobody
 * has spare attention for at hour six.
 *
 * Reading downwards: what the regulations allow, what the team intended, what
 * the car did. A stop that landed outside a window, a stint that ran past its
 * cap, and a window that came and went with nobody stopping are then shapes
 * rather than numbers somebody has to notice.
 */
export default function PlannerScreen({
  data,
  car,
  now,
  context,
}: {
  data: DashboardData
  car: string | null
  now: Date
  context: ScreenContext
}) {
  const plan = context.plan ?? null
  const regime = plan?.regime
  // `at` stops when the session does, so a finished race does not keep growing
  // its last driver stint for ever.
  const { stints: all, at } = useDriverStints(car, data, context, now)
  // Green is for a stint happening now. A finished race's last stint is
  // history like the rest. Judged on the dashboard's clock, as the stint chart
  // does — the browser's, or the replayed instant; see isCaptureLive.
  const live = !data.session?.finishedAt && isCaptureLive(data.session?.lastSeenAt?.toDate(), now)

  /*
   * What the plan is about, in time.
   *
   * Everything on this screen is measured against it, and the reason it has to
   * be stated is that nothing else here is bound to the race. The dashboard
   * shows whichever session is freshest, which on the morning of a race is
   * yesterday's qualifying — so the stints, the clock start and the budget all
   * arrive from a session the plan has nothing to do with.
   */
  const frame = useMemo(() => {
    if (plan?.startsAt == null) return null
    return { from: plan.startsAt, to: plan.endsAt ?? Number.POSITIVE_INFINITY }
  }, [plan])

  /*
   * Only the stints that belong to this race.
   *
   * Without this the car's row filled with slivers: every stint of yesterday's
   * qualifying sits before the axis begins, so each was clamped to the left
   * edge and drawn at its minimum width, a stack of small rectangles beside the
   * car number meaning nothing.
   *
   * The drawing was the visible half. The budget was the other: driverBudgets
   * counts time towards a cumulative cap, and an eight-and-a-half hour
   * allowance had yesterday's running already taken out of it before today's
   * race began.
   */
  const stints = useMemo(() => {
    if (!frame) return all
    return all.filter(
      (s) => (s.to ?? at).getTime() >= frame.from && s.from.getTime() <= frame.to,
    )
  }, [all, frame, at])

  const windows = useMemo(() => {
    if (!plan) return []
    /*
     * The stream's own clock start, but only if it could be this race's.
     *
     * frameFor prefers it because a race that started eight minutes late really
     * did start eight minutes late. That is only an improvement when the
     * session on screen IS the race: yesterday's qualifying reports a clock
     * start from yesterday afternoon, and an elapsed-anchored plan resolved
     * against it would put every window on the wrong day.
     */
    const reported = data.session?.clockStartAt?.toDate() ?? null
    const plausible =
      reported != null &&
      frame != null &&
      // An hour's grace before the planned start, which covers a race brought
      // forward or a clock armed early, and excludes another day entirely.
      reported.getTime() >= frame.from - 3_600_000 &&
      reported.getTime() <= frame.to
    return resolveWindows(plan.regime, frameFor(plan, plausible ? reported : null))
  }, [plan, data.session, frame])

  /*
   * The axis is the race, and it does not move.
   *
   * It begins at the plan's own start and never earlier. It used to include
   * "now" among the instants it spanned, which reads as reasonable and is not:
   * before the race, "now" is earlier than the start, and on a laptop showing a
   * session that stopped updating yesterday afternoon the chart opened at
   * yesterday afternoon and the race was a sliver at the right-hand end.
   *
   * Nor does the left edge advance as the race runs. A plan is a fixed shape
   * and its whole value is being read against a fixed shape: a window three
   * hours away has to stay three hours away as you watch it approach. Where
   * you are is the "now" line moving across it, which is the thing that should
   * move.
   *
   * The end does grow, because a race can overrun its plan and the car's own
   * stints are the evidence of it. Nothing shrinks the start.
   */
  const span = useMemo(() => {
    const instants: number[] = []
    if (plan?.endsAt != null) instants.push(plan.endsAt)
    for (const w of windows) instants.push(w.opens.getTime(), w.closes.getTime())
    for (const s of stints) instants.push(s.from.getTime(), (s.to ?? at).getTime())
    // Only once the race has started. Before it, "now" is behind the start and
    // would drag the axis back to it.
    if (plan?.startsAt != null && at.getTime() > plan.startsAt) instants.push(at.getTime())

    const full = spanning(instants, at)
    return plan?.startsAt != null ? { from: plan.startsAt, to: Math.max(full.to, plan.startsAt + 1) } : full
  }, [plan, windows, stints, at])

  /*
   * The drivers entered in this car, not everybody who has ever raced for the
   * team.
   *
   * The same narrowing the stint chart and the stop prompt already do. It
   * matters more here than anywhere: the budget divides mandatory stops between
   * drivers, so counting a club roster of five against an entry of three turns
   * "at most four stints each" into "at most three", and that is a number
   * somebody plans a race around.
   *
   * Falling back to the whole roster where the entry says nothing, because an
   * empty picker is worse than a long one.
   */
  const planDrivers = enteredDrivers(context, car)

  const report = driverBudgets(stints, regime, planDrivers, at)
  const state = windowStateAt(windows, at)
  const nameOf = (id: string | null) => (id ? (driverName(context, id) ?? 'Unknown driver') : null)

  if (!plan) {
    return (
      <div className="stints-empty">
        <p>
          No race plan for this event yet. Make one from the event&rsquo;s page &mdash; a name and a
          start time is enough &mdash; and this shows it against what the car actually does.
        </p>
      </div>
    )
  }

  if (!car) {
    return (
      <div className="stints-empty">
        <p>Pick a car. A plan is about one entry, and this screen follows whichever it is.</p>
      </div>
    )
  }

  const ticks = hourTicks(span)
  const planned = plannedStints(windows, plan.drivers, span.from, span.to)

  return (
    <div className="planner">
      <PlanHeader
        plan={plan}
        windows={windows}
        state={state}
        report={report}
        drivers={planDrivers.length}
      />

      <div className="stints-scroll">
        <div className="stints-grid" style={{ ['--stint-width' as string]: `${axisWidth(span)}px` }}>
          <div className="stints-axis">
            <div className="stints-axis-label" />
            <div className="stints-axis-track">
              {ticks.map((t) => (
                <div
                  key={t.at}
                  className="stints-tick"
                  style={{ left: `${pct(t.at, span)}%` }}
                  data-major={t.major || undefined}
                >
                  <span>{t.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* The same track-state band the stint chart carries, because a
              window missed under a safety car is a different story from one
              missed in green running. */}
          <div className="stints-band">
            <div className="stints-axis-label">Track</div>
            <div className="stints-axis-track stints-band-track">
              {data.fcyPeriods.map((p, i) => {
                const from = p.startedAt?.toDate().getTime()
                if (from == null) return null
                const to = p.endedAt?.toDate().getTime() ?? span.to
                return (
                  <div
                    key={i}
                    className="stints-fcy"
                    data-kind={(p.state ?? '').toLowerCase()}
                    style={{
                      left: `${pct(from, span)}%`,
                      width: `${pct(to, span) - pct(from, span)}%`,
                    }}
                    title={`${p.state ?? 'FCY'} ${formatClock(new Date(from), { seconds: true })}`}
                  />
                )
              })}
            </div>
          </div>

          {windows.length > 0 && (
            <div className="stints-row">
              <div className="stints-axis-label">Windows</div>
              <div className="stints-axis-track">
                {windows.map((w) => {
                  const left = pct(w.opens.getTime(), span)
                  const right = pct(w.closes.getTime(), span)
                  // A window that has closed with no change of ours inside it.
                  // Sometimes deliberate — a series may run more windows than
                  // mandatory stops — so it is marked, not alarmed about.
                  const missed = w.closes.getTime() <= at.getTime() && !stopInside(stints, w)
                  const openNow = state.open?.index === w.index
                  return (
                    <div
                      key={w.index}
                      className="planner-window"
                      data-state={
                        openNow ? 'open' : w.closes.getTime() <= at.getTime() ? 'past' : 'future'
                      }
                      data-missed={missed || undefined}
                      style={{ left: `${left}%`, width: `${Math.max(right - left, 0.3)}%` }}
                      title={`${w.label ?? `Window ${w.index}`}: ${formatClock(w.opens)}–${formatClock(
                        w.closes,
                      )}${missed ? ' — no stop made' : ''}`}
                    >
                      <span className="planner-window-num">{w.label ?? w.index}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {planned.length > 0 && (
            <div className="stints-row">
              <div className="stints-axis-label">Plan</div>
              <div className="stints-axis-track">
                {planned.map((p, i) => (
                  <div
                    key={i}
                    className="planner-planned"
                    style={{
                      left: `${pct(p.from, span)}%`,
                      width: `${Math.max(pct(p.to, span) - pct(p.from, span), 0.4)}%`,
                    }}
                    title={`Planned stint ${i + 1}: ${nameOf(p.driverId) ?? 'nobody assigned'}`}
                  >
                    <span>{nameOf(p.driverId) ?? '—'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="stints-row" data-mine>
            <div className="stints-axis-label stints-car">{car}</div>
            <div className="stints-axis-track">
              {stints.map((s, i) => (
                <ActualStint
                  key={i}
                  stint={s}
                  index={i}
                  now={at}
                  running={s.to === null && live}
                  span={span}
                  windows={windows}
                  regime={regime}
                  name={nameOf(s.driverId) ?? s.name ?? null}
                />
              ))}
              {/* An empty row before the race is the ordinary case, and an
                  unexplained blank one reads as something failing to load. */}
              {stints.length === 0 && (
                <span className="planner-nothing">
                  {frame && at.getTime() < frame.from
                    ? `Nothing yet — this race starts at ${formatClock(new Date(frame.from))}`
                    : 'Nothing from this car in the race yet'}
                </span>
              )}
              <div className="planner-now" style={{ left: `${pct(at.getTime(), span)}%` }} />
            </div>
          </div>
        </div>
      </div>

      <DriverTable report={report} regime={regime} stints={stints} nameOf={nameOf} />
    </div>
  )
}

/**
 * One driver stint as it actually ran.
 *
 * Interior pit visits are drawn *on* the card rather than as gaps between
 * cards, because that is precisely what a driver stint is: a span the clock
 * does not stop during. A card with two ticks in it and no break is the visual
 * answer to "why is he already at an hour twenty".
 */
function ActualStint({
  stint,
  index,
  now,
  running,
  span,
  windows,
  regime,
  name,
}: {
  stint: DriverStint
  index: number
  now: Date
  /** The car's current stint in a session still being captured. */
  running: boolean
  span: Span
  windows: readonly ResolvedWindow[]
  regime: Regime | undefined
  name: string | null
}) {
  const from = stint.from.getTime()
  const to = (stint.to ?? now).getTime()
  const left = pct(from, span)
  const width = Math.max(pct(to, span) - left, 0.4)

  const length = stintDuration(stint, now)
  const cap = regime?.stint?.maxDurationMs
  const over = cap != null && length > cap
  // A change made outside every window. Not necessarily illegal — a repair, or
  // a series where changes are free — but it is the thing that makes the count
  // rules stop adding up, so it is flagged for a human rather than absorbed.
  const outside =
    stint.to != null && windows.length > 0 && !windows.some((w) => within(w, stint.to as Date))

  return (
    <div
      className="planner-actual"
      data-tone={running ? 'open' : `c${index % 4}`}
      data-over={over || undefined}
      data-outside={outside || undefined}
      style={{ left: `${left}%`, width: `${width}%` }}
      title={[
        `Stint ${index + 1}: ${name ?? 'nobody named'}${stint.declared ? ' (your team said so)' : name ? ' (timing system)' : ''}`,
        `${formatClock(stint.from)} – ${stint.to ? formatClock(stint.to) : 'running'}`,
        `${formatDuration(length)}${cap != null ? ` of ${formatDuration(cap)}` : ''}`,
        outside ? 'Driver change made outside a window' : null,
        stint.uncertain ? 'Times inferred rather than observed' : null,
      ]
        .filter(Boolean)
        .join('\n')}
    >
      <div className="planner-actual-name">
        {name ?? '—'}
        {stint.uncertain && (
          <span className="planner-doubt" title="Times inferred rather than observed">
            ?
          </span>
        )}
      </div>
      <div className="planner-actual-len">
        {formatDuration(length)}
        {stint.to === null && '+'}
      </div>
      {stint.visitsInside.map((v, j) => (
        <div
          key={j}
          className="planner-visit"
          style={{ left: `${((v.pitIn.getTime() - from) / Math.max(to - from, 1)) * 100}%` }}
          title={`Pit visit, no driver change — ${formatDuration(
            v.pitOut.getTime() - v.pitIn.getTime(),
          )}`}
        />
      ))}
    </div>
  )
}

/**
 * Whether the plan can be run at all.
 *
 * Arithmetic nobody does by hand and everybody assumes somebody else did. With
 * twelve stints, three drivers and a cumulative cap it is not obvious that
 * somebody breaches, and it is the sort of thing discovered at four in the
 * morning rather than on the Tuesday before.
 */
function PlanHeader({
  plan,
  windows,
  state,
  report,
  drivers,
}: {
  plan: NonNullable<ScreenContext['plan']>
  windows: readonly ResolvedWindow[]
  state: ReturnType<typeof windowStateAt>
  report: ReturnType<typeof driverBudgets>
  drivers: number
}) {
  const length = plan.startsAt != null && plan.endsAt != null ? plan.endsAt - plan.startsAt : null
  const cap = plan.regime.driver?.maxTotalDrivingMs
  const maxStint = plan.regime.stint?.maxDurationMs
  const expected = expectedStints(plan.regime)

  const notes: { text: string; bad?: boolean }[] = []

  if (expected && drivers > 0) {
    notes.push({
      text: `${expected} stints across ${drivers} drivers — at most ${stintCeiling(
        expected,
        drivers,
      )} each`,
    })
  }
  if (length && cap && drivers > 0) {
    const capacity = cap * drivers
    notes.push(
      capacity < length
        ? {
            text: `${drivers} drivers cover ${formatDuration(
              capacity,
            )} between them and the race is ${formatDuration(length)} — not enough crew`,
            bad: true,
          }
        : { text: `${formatDuration(capacity - length)} of driving-time slack across the crew` },
    )
  }
  if (length && maxStint) {
    notes.push({
      text: `at least ${Math.ceil(length / maxStint)} stints to cover ${formatDuration(length)}`,
    })
  }
  if (plan.regime.windows?.anchor === 'elapsed') {
    notes.push({ text: 'windows follow the session clock, so a stoppage moves them' })
  }

  return (
    <div className="planner-head">
      <div className="planner-head-main">
        <span className="planner-name">{plan.name}</span>
        <span className="planner-state">
          {windows.length === 0
            ? 'No windows — stop when it suits'
            : state.open
              ? `Window ${state.open.label ?? state.open.index} open until ${formatClock(
                  state.open.closes,
                )}`
              : state.next
                ? `${state.passed} of ${windows.length} gone · next opens ${formatClock(state.next.opens)}`
                : `All ${windows.length} windows gone`}
        </span>
      </div>
      {notes.length > 0 && (
        <div className="planner-notes">
          {notes.map((n) => (
            <span key={n.text} className="planner-note" data-bad={n.bad || undefined}>
              {n.text}
            </span>
          ))}
        </div>
      )}
      {report.incomplete && (
        <div className="planner-notes">
          <span className="planner-note" data-bad>
            {report.unattributed} stint{report.unattributed === 1 ? '' : 's'} with nobody named —
            stint counts cannot be worked out until every one has a driver.
          </span>
        </div>
      )}
    </div>
  )
}

function DriverTable({
  report,
  regime,
  stints,
  nameOf,
}: {
  report: ReturnType<typeof driverBudgets>
  regime: Regime | undefined
  stints: readonly DriverStint[]
  /**
   * Names from the whole roster, not the entry.
   *
   * The budget is reckoned against the crew entered in the car, but somebody
   * who actually drove and was never on the entry still has a row here — their
   * time counts whatever the paperwork said — and that row has to carry their
   * name rather than "Unknown driver".
   */
  nameOf: (id: string | null) => string | null
}) {
  if (report.drivers.length === 0) return null
  return (
    <div className="planner-drivers">
      {report.drivers.map((d) => {
        const blocked = cannotDrive(d, report, regime, stints)
        return (
          <div
            key={d.driverId}
            className="planner-driver"
            data-current={d.current || undefined}
            data-over={d.over || undefined}
          >
            <span className="planner-driver-name">
              {d.name ?? nameOf(d.driverId) ?? 'Unknown driver'}
            </span>
            <span className="planner-driver-num">{formatDuration(d.usedMs)} driven</span>
            {d.remainingMs != null && (
              <span className="planner-driver-num">
                {formatDuration(Math.max(0, d.remainingMs))} left
              </span>
            )}
            {!report.incomplete && d.stintCeiling != null && (
              <span className="planner-driver-num">
                {d.stints} of {d.stintCeiling} stints
              </span>
            )}
            {blocked && <span className="planner-driver-blocked">{blocked}</span>}
          </div>
        )
      })}
    </div>
  )
}

// ------------------------------------------------------------------ maths

function within(w: ResolvedWindow, at: Date): boolean {
  const t = at.getTime()
  return w.opens.getTime() <= t && t <= w.closes.getTime()
}

/** Whether any of our driver changes happened inside this window. */
function stopInside(stints: readonly DriverStint[], w: ResolvedWindow): boolean {
  return stints.some((s) => s.to != null && within(w, s.to))
}

